import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import DropdownCheckbox from '@/storybook/components/FormCollection/components/DropdownCheckbox/DropdownCheckbox';
import Badge from '@/storybook/components/Badge/Badge';
import type { TreeItemLevel } from '@/storybook/components/FormCollection/TreeSelect/Components/TreeItem/TreeItem.types';
import LeafRow from '../Components/LeafRow';
import {
  NestedOptions,
  type NestedTreeNode,
  getSelectedCountInSubtree,
  getBadgeForIndicationMode,
  getAncestorPaths,
} from '../TreeSelect.helpers';

export type VisibleTreeRow = {
  optionLabel: string;
  properties: NestedTreeNode;
  level: TreeItemLevel;
  hasSubOptions: boolean;
  isCurrentBranchOpen: boolean;
  isDisabled: boolean;
};

interface BuildVisibleTreeRowsParams {
  nestedOptions: NestedOptions;
  searchInput: string;
  searchResults: ReadonlySet<string>;
  branchOverrides: ReadonlyMap<string, boolean>;
  openParentPathsSet: ReadonlySet<string>;
  disabled?: boolean;
  disabledPathSet?: ReadonlySet<string>;
}

function getTreeItemLevel(depth: number): TreeItemLevel {
  if (depth === 0) return 1;
  if (depth === 1) return 2;
  if (depth === 2) return 3;
  if (depth === 3) return 4;
  return 5;
}

function buildVisibleTreeRows({
  nestedOptions,
  searchInput,
  searchResults,
  branchOverrides,
  openParentPathsSet,
  disabled = false,
  disabledPathSet,
}: BuildVisibleTreeRowsParams): VisibleTreeRow[] {
  const rows: VisibleTreeRow[] = [];

  const appendVisibleRows = (currentOptions: NestedOptions, depth: number) => {
    for (const [optionLabel, properties] of Object.entries(currentOptions)) {
      if (searchInput && !searchResults.has(properties.path)) continue;

      const hasSubOptions = Object.keys(properties.subOptions).length > 0;
      const isInSearchResults = Boolean(searchInput) && searchResults.has(properties.path);
      const userOverride = branchOverrides.get(properties.path);
      const isCurrentBranchOpen = isInSearchResults || (userOverride ?? openParentPathsSet.has(properties.path));

      rows.push({
        optionLabel,
        properties,
        level: getTreeItemLevel(depth),
        hasSubOptions,
        isCurrentBranchOpen,
        isDisabled: disabled || (disabledPathSet?.has(properties.path) ?? false),
      });

      if (hasSubOptions && isCurrentBranchOpen) appendVisibleRows(properties.subOptions, depth + 1);
    }
  };

  appendVisibleRows(nestedOptions, 0);
  return rows;
}

type UseTreeSelectRenderingParams<T extends string> = {
  nestedOptions: NestedOptions;
  value: T[];
  searchInput: string;
  searchResults: Set<string>;
  branchOverrides: Map<string, boolean>;
  selectionMode: 'standard' | 'indication';
  disabled?: boolean;
  disabledPathSet?: Set<string>;
  hideCheckboxes?: boolean;
  treeListItemClassName?: string;
  handleClickOption: (optionPath: string) => void;
  handleToggleBranch: (optionPath: string, currentState: boolean) => void;
};

export function useTreeSelectRendering<T extends string>({
  nestedOptions,
  value,
  searchInput,
  searchResults,
  branchOverrides,
  selectionMode,
  disabled,
  disabledPathSet,
  hideCheckboxes,
  treeListItemClassName,
  handleClickOption,
  handleToggleBranch,
}: UseTreeSelectRenderingParams<T>) {
  const { t } = useTranslation();
  const disabledTooltip = t('components.dropdownPanel.valueNoLongerApplicable');
  // Pre-compute the set of ancestor paths that have selected descendants.
  const openParentPathsSet = useMemo(() => {
    const parents = new Set<string>();
    if (!value || value.length === 0) return parents;
    for (const selectedPath of value) {
      for (const ancestor of getAncestorPaths(selectedPath)) {
        parents.add(ancestor);
      }
    }
    return parents;
  }, [value]);

  const selectedSet = useMemo(() => new Set<string>(value ?? []), [value]);

  const visibleRows = useMemo(
    () =>
      buildVisibleTreeRows({
        nestedOptions,
        searchInput,
        searchResults,
        branchOverrides,
        openParentPathsSet,
        disabled,
        disabledPathSet,
      }),
    [nestedOptions, searchInput, searchResults, branchOverrides, openParentPathsSet, disabled, disabledPathSet],
  );

  const renderTreeRow = useCallback(
    (
      { optionLabel, properties, level, hasSubOptions, isCurrentBranchOpen, isDisabled }: VisibleTreeRow,
      tabIndex?: number,
    ) => {
      const selectedCount = getSelectedCountInSubtree(properties, selectedSet, selectionMode === 'indication');
      const checked = selectedSet.has(properties.path);
      const isPathDisabled = disabledPathSet?.has(properties.path) ?? false;

      const indicationBadge = getBadgeForIndicationMode({
        isIndication: selectionMode === 'indication',
        selectedSet,
        properties,
        hasSubOptions,
        isCurrentBranchOpen,
        selectedCount,
      });
      // Selected-but-disabled nodes show a grey "0" badge (no data for the
      // current combination) instead of the blue indication count badge.
      const badgeContent =
        isPathDisabled && checked ? (
          <Badge size="S" variant="light" color="grey" data-testid="cascadingItemBadge">
            0
          </Badge>
        ) : (
          indicationBadge
        );

      const rowTooltip = isPathDisabled ? disabledTooltip : undefined;

      return hasSubOptions ? (
        <DropdownCheckbox
          label={optionLabel}
          level={level}
          tabIndex={tabIndex}
          optionPath={properties.path}
          onCheck={handleClickOption}
          onOpenDropdown={handleToggleBranch}
          isOpen={isCurrentBranchOpen}
          checked={checked}
          className={treeListItemClassName}
          rightAdornment={badgeContent}
          hideCheckbox={hideCheckboxes}
          isDisabled={isDisabled}
          tooltipText={rowTooltip}
          forceTooltip={isPathDisabled && checked}
        />
      ) : (
        <LeafRow
          optionLabel={optionLabel}
          level={level}
          tabIndex={tabIndex}
          properties={{ path: properties.path, checked }}
          handleClickOption={handleClickOption}
          badgeContent={badgeContent}
          hideCheckbox={hideCheckboxes}
          isDisabled={isDisabled}
          tooltipText={rowTooltip}
          forceTooltip={isPathDisabled && checked}
        />
      );
    },
    [
      handleClickOption,
      handleToggleBranch,
      selectionMode,
      selectedSet,
      hideCheckboxes,
      treeListItemClassName,
      disabledPathSet,
      disabledTooltip,
    ],
  );

  return { visibleRows, renderTreeRow };
}
